/**
 * `/admin/studies` — LATO staff's all-organization studies board, in the Admin area. Filters
 * (organization, search, archived) are URL state; cards open `/admin/studies/{id}` in the
 * Admin shell. The data comes from the admin-scoped `/admin/studies` API — the workspace `/studies`
 * list never becomes global because the caller is staff.
 */

import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AdminStudiesBoard } from "@/features/studies/AdminStudiesBoard";
import { StudiesPageLayout } from "@/features/studies/StudiesPageLayout";
import { NewStudyAction } from "@/features/studies/StudiesToolbar";
import {
  dropRetiredListParams,
  parseStudiesListSearch,
  rememberStudiesListSearch,
  type StudiesListSearch,
} from "@/features/studies/studiesList";
import { StudySurfaceProvider } from "@/features/studies/studySurface";

function AdminStudies() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  // "Studies" from inside an admin study comes back to exactly this list.
  useEffect(() => rememberStudiesListSearch(search, "admin"), [search]);

  return (
    <StudySurfaceProvider value="admin">
      <StudiesPageLayout description="Every organization's studies. Filter by organization or name.">
        <AdminStudiesBoard
          // An explicitly selected organization pre-fills the new study's owner.
          action={<NewStudyAction to="/admin/studies/new" org={search.org} />}
          search={search}
          onSearchChange={(next: StudiesListSearch) =>
            void navigate({ to: "/admin/studies", search: next, replace: true })
          }
        />
      </StudiesPageLayout>
    </StudySurfaceProvider>
  );
}

export const Route = createFileRoute("/_auth/admin/studies/")({
  validateSearch: (search: Record<string, unknown>): StudiesListSearch =>
    parseStudiesListSearch(search),
  // Old bookmarks' retired `status` param is stripped from the URL (the columns group by status).
  search: { middlewares: [dropRetiredListParams] },
  component: AdminStudies,
});
